import { useSyncExternalStore } from "react";

// The videos (and music files) a Premium member picks on the My Videos page. They stay on the member's own device: the browser reads the file
// where it is, nothing is uploaded and nothing is saved on the site. The list lives in memory only, so closing or refreshing
// the page forgets it, and signing out (or in as someone else) empties it (PlayerContext does that).
// Each file becomes a "song" the big player can play like any other (its title is the file's name, "local: true" tells the
// player there is nothing on the site to look up: no play count, no playlist, no share link, no captions from the server).
export const MAX_FILES = 200;

// A guest or a free member may try ONE of their own videos for 10 seconds, then they are asked to create a login or go Premium.
// `id` is the video that took the try; once it has run its 10 seconds (or ended), nothing else plays until the page is reloaded
// or a different person signs in. The player (PlayerContext) cuts the video off and shows the box.
export const SAMPLE_SECONDS = 10;
export const sample = { id: null, done: false };

let files = [];            // [{ key, name, size, url }]
let counter = 0;
const seen = new Set();    // name|size|date of the files already in the list, so the same file is not added twice
const listeners = new Set();
const emit = () => { listeners.forEach((f) => f()); };

// A music file (MP3, WAV and the like) has no picture of its own, so it shows the Dan The Man TV astronaut with the flag (the same
// picture the site uses when a link is shared).
export const MUSIC_COVER = "https://media.danthemantv.com/dtm-share.jpg";

// a plain picture for the cover: a dark steel square with a play mark
const COVER = `data:image/svg+xml;utf8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120"><rect width="120" height="120" fill="#12151a"/><rect x="6" y="6" width="108" height="108" rx="14" fill="none" stroke="#3b4652" stroke-width="3"/><path d="M47 38l34 22-34 22z" fill="#9fb3c4"/></svg>')}`;

const isVideo = (f) => /^video\//.test(f.type || "") || /\.(mp4|m4v|mov|webm)$/i.test(f.name || "");
export const isMusic = (f) => /^audio\//.test(f.type || "") || /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac)$/i.test(f.name || "");
const titleOf = (name) => String(name || "Video").replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").replace(/\s+/g, " ").trim() || "Video";

// Adds the picked files. Returns how many went in and why the others did not.
export function addFiles(list) {
  const out = { added: 0, notVideo: 0, same: 0, full: 0 };
  Array.from(list || []).forEach((f) => {
    const music = !isVideo(f) && isMusic(f);
    if (!music && !isVideo(f)) { out.notVideo += 1; return; }
    const sig = `${f.name}|${f.size}|${f.lastModified}`;
    if (seen.has(sig)) { out.same += 1; return; }
    if (files.length >= MAX_FILES) { out.full += 1; return; }
    seen.add(sig);
    counter += 1;
    files = [...files, { key: counter, sig, name: f.name, size: f.size || 0, music, url: URL.createObjectURL(f) }];
    out.added += 1;
  });
  if (out.added) emit();
  return out;
}

export function removeFile(key) {
  const f = files.find((x) => x.key === key);
  if (!f) return;
  seen.delete(f.sig);
  files = files.filter((x) => x.key !== key);
  emit();
}

// Remove every file from the list. (The player may still be playing one of them, so the addresses are not revoked here.)
export function emptyList() {
  if (!files.length) return;
  files.forEach((f) => seen.delete(f.sig));
  files = [];
  emit();
}

// Sign-out or a different person: forget everything and let go of the file addresses.
export function clearFiles() {
  sample.id = null; sample.done = false;
  if (!files.length) return;
  files.forEach((f) => { try { URL.revokeObjectURL(f.url); } catch { /* already gone */ } });
  files = [];
  seen.clear();
  emit();
}

// The list as songs the player can play
export function songOf(f) {
  return { id: `local-${f.key}`, local: true, music: !!f.music, title: titleOf(f.name), artist: f.music ? "My Music" : "My Videos", album: "", video_url: f.url, cover_url: f.music ? MUSIC_COVER : COVER, locked: false, age_locked: false };
}

const subscribe = (fn) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export const useMyFiles = () => useSyncExternalStore(subscribe, () => files, () => files);
export const sizeText = (n) => (n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : n >= 1e6 ? `${Math.round(n / 1e6)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
